<script setup>
import VTooltip from '@/components/elements/v-tooltip.vue';
import VPopover from '@/components/elements/v-popover.vue';
import VCopyText from '@/components/elements/v-copy-text.vue';
import { useEventListener } from '@/directive/use-event-listener';
import { computed, ref, onMounted, useTemplateRef } from 'vue';

defineProps({
  label: { type: String, default: null },
  placeholder: { type: String, default: '--' },
  tooltip: { type: String, default: null }, // {filled: Boolean, position: String, text: String}
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary'].includes(value)
  }
});

const valueRef = useTemplateRef('copy-text-ref');
const valueScrollWidth = ref(0);

const hasTooltip = computed(() => {
  const clientWidth = valueRef.value?.copyTextRef.clientWidth;
  return valueScrollWidth.value > clientWidth;
});

onMounted(() => {
  if (!valueRef.value?.copyTextRef) return;
  valueScrollWidth.value = valueRef.value.copyTextRef.scrollWidth;
});

useEventListener(window, 'resize', () => {
  if (!valueRef.value?.copyTextRef) return;
  valueScrollWidth.value = valueRef.value.copyTextRef.scrollWidth;
});
</script>

<template>
  <div class="v-label-value" :class="`v-label-value--${variant}`">
    <div class="label-wrapper">
      <v-tooltip size="small">
        <template #trigger>
          <div class="label">
            {{ label }}
          </div>
        </template>
        {{ tooltip }}
      </v-tooltip>
    </div>
    <v-popover size="small">
      <template #trigger="{ show, hide }">
        <v-copy-text
          ref="copy-text-ref"
          class="value ellipsis"
          @mouseenter="hasTooltip ? show() : undefined"
          @mouseleave="hasTooltip ? hide() : undefined"
          @focus="hasTooltip ? show() : undefined"
          @blur="hasTooltip ? hide() : undefined"
          :tabindex="hasTooltip ? 0 : undefined"
        >
          <slot>
            <div class="placeholder">{{ placeholder }}</div>
          </slot>
        </v-copy-text>
      </template>
      <div v-if="hasTooltip" class="v-label-value__value-tooltip">
        <slot></slot>
      </div>
    </v-popover>
  </div>
</template>

<style lang="scss">
.v-label-value {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xsmall);
  .label-wrapper {
    display: flex;

    .label {
      padding: 0;
      color: var(--color-text-subtle);
      font-size: var(--type-small-body);
      line-height: var(--type-small-headline);
      white-space: nowrap;
    }
  }

  .value {
    height: 100%;
    padding: 0;
    font-size: var(--type-medium-headline);
    color: var(--color-text-default);
  }

  &__value-tooltip {
    white-space: normal;
    background: var(--color-bg-level-01);
    border: var(--border-stroke-default) solid
      var(--color-border-strong-emphasis);
    padding: var(--spacing-xsmall) var(--spacing-small);
    border-radius: var(--spacing-xsmall);
    color: var(--color-text-strong-on-dark);
    max-width: 400px;
    margin: 0 var(--spacing-small);
  }

  // Sub classes

  &.success {
    .label {
      color: var(--color-success-text-default);
    }
  }

  &.error {
    .label {
      color: var(--color-error-text-default);
    }
  }

  &.blue {
    .label {
      color: var(--color-info-text-default);
    }
  }

  &--secondary {
    .value {
      font-size: var(--type-small-body);
    }
  }
}
</style>
